import React, { useEffect, useState, useRef } from 'react';
import useClickOutside from '../../Hooks/useClickOutside';
import SystemDataService from '../../Services/SystemDataService';
import Image from '../Image/image-component';
import VideoComponent from '../Video/video-component';
import { Modal } from './modal-component';

const AssetPickerModal = ({ open, onClose, onSelect, dir, strings }) => {
    const [assets, setAssets] = useState([]);
    const [previewMedia, setPreviewMedia] = useState(null); // State for the preview overlay
    const contentRef = useRef(null);

    useClickOutside(contentRef, () => {
        if (open && !previewMedia) onClose();
    });

    useEffect(() => {
        if (open) fetchAssets();
    }, [open]);

    const fetchAssets = async () => {
        const data = await SystemDataService.getByPath('mediaRegistry');
        setAssets(Array.isArray(data) ? data : []);
    };

    if (!open) return null;

    return (
        <Modal expand={open}>
            <div 
                ref={contentRef}
                dir={dir}
                className="relative w-full lg:max-w-5xl bg-white dark:bg-[#0c0c0c] rounded-2xl shadow-2xl flex flex-col my-6 h-[85vh] overflow-hidden border border-neutral-200 dark:border-neutral-800 font-primary"
            >
                {/* --- FULL SCREEN PREVIEW OVERLAY --- */}
                {previewMedia && (
                    <div className="absolute inset-0 z-[60] bg-white/80 dark:bg-black/90 backdrop-blur-xl animate-fadeIn flex flex-col">
                        <div className="p-6 flex justify-between items-center">
                            <span className="text-[10px] font-bold tracking-widest text-neutral-400 uppercase">Media Preview</span>
                            <button 
                                onClick={() => setPreviewMedia(null)}
                                className="p-2 bg-neutral-100 dark:bg-neutral-800 rounded-full text-neutral-500 hover:text-red-500 transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <div className="flex-grow flex items-center justify-center p-8 lg:p-20">
                            {previewMedia.toLowerCase().match(/\.(mp4|webm|mov)$/) ? (
                                <video src={previewMedia} controls autoPlay className="max-w-full max-h-full rounded-xl shadow-2xl" />
                            ) : (
                                <img src={previewMedia} alt="Preview" className="max-w-full max-h-full object-contain rounded-xl shadow-2xl" />
                            )}
                        </div>
                        <div className="p-8 flex justify-center">
                            <button 
                                onClick={() => { onSelect(previewMedia); setPreviewMedia(null); onClose(); }}
                                className="px-12 py-4 bg-blue-600 text-white rounded-full font-bold shadow-xl hover:bg-blue-700 transition-all "
                            >
                                {strings.gallery.selectImage}
                            </button>
                        </div>
                    </div>
                )}

                {/* --- HEADER --- */}
                <div className="px-8 py-5 border-b border-neutral-100 dark:border-neutral-800 flex justify-between items-center bg-white dark:bg-[#0c0c0c] shrink-0">
                    <div>
                        <h2 className="text-xl font-bold text-neutral-900 dark:text-white">
                            {strings.gallery.title}
                        </h2>
                        <span className="text-[11px] font-medium text-neutral-400">
                            {assets.length} {strings.card.itemsCount}
                        </span>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-400 transition-colors">
                         <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                {/* --- GALLERY GRID --- */}
                <div className="flex-grow overflow-y-auto p-6 bg-[#fafafa] dark:bg-[#080808] custom-scrollbar">
                    <p className="text-[13px] text-neutral-500 mb-6 font-medium">
                        {strings.gallery.subtitle}
                    </p>

                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
                        {assets.map((asset) => {
                            const mediaPath = asset.image || "";
                            const isVideo = mediaPath.toLowerCase().match(/\.(mp4|webm|mov)$/);
                            
                            return (
                                <div
                                    key={asset.id}
                                    className="group relative aspect-square rounded-xl bg-white dark:bg-[#111] border border-neutral-200 dark:border-neutral-800 overflow-hidden cursor-pointer hover:border-blue-500 hover:shadow-xl transition-all duration-300"
                                >
                                    {isVideo ? (
                                        <VideoComponent video={mediaPath} className="w-full h-full object-cover" useSekeleton={true} />
                                    ) : (
                                        <Image src={mediaPath} wrapperClassName="absolute inset-0 w-full h-full" className="w-full h-full object-cover p-1 transition-transform duration-500 " useSekeleton={true} />
                                    )}

                                    {/* Selection/Select Overlay */}
                                    <div 
                                        onClick={() => { onSelect(mediaPath); onClose(); }}
                                        className="absolute inset-0 bg-blue-600/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2"
                                    >
                                        <span className="text-[10px] font-bold text-white uppercase  px-3 py-1 bg-black/20 backdrop-blur-sm rounded-full">
                                            {strings.gallery.selectImage}
                                        </span>
                                    </div>

                                    {/* PREVIEW BUTTON (Eye Icon) */}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setPreviewMedia(mediaPath); }}
                                        className="absolute bottom-2 right-2 z-20 p-2 bg-white/90 dark:bg-black/70 text-neutral-700 dark:text-white rounded-md shadow-sm border border-neutral-100 dark:border-neutral-800 opacity-0 group-hover:opacity-100 hover:bg-white dark:hover:bg-black transition-all transform translate-y-2 group-hover:translate-y-0"
                                        title="Preview"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                    </button>

                                    {asset.name && (
                                        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                            <span className="text-[8px] text-white truncate block font-mono">{asset.name}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        
                        {assets.length === 0 && (
                            <div className="col-span-full py-32 text-center">
                                <div className="inline-flex p-4 rounded-full bg-neutral-100 dark:bg-neutral-900 mb-4 text-neutral-300">
                                    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                </div>
                                <p className="text-sm font-bold text-neutral-400 max-w-sm mx-auto leading-relaxed uppercase italic">{strings.gallery.noAssets}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* --- FOOTER --- */}
                <div className="px-8 py-4 border-t border-neutral-100 dark:border-neutral-800 bg-white dark:bg-[#0c0c0c] flex justify-end shrink-0">
                    <button onClick={onClose} className="text-[12px] font-bold text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors">{strings.modal.cancel}</button>
                </div>
            </div>
        </Modal>
    );
};

export default AssetPickerModal;